import { ReactNode, useEffect, useState } from "react";
import { ThemeSelectorContext } from "@/context";
import { THEMES, ThemeConfig } from "@/constants";
import { isPrime } from "@/utils";
import { getAppThemeAPI } from "../../api/get";

interface ThemeSelectorProviderProps {
  children: ReactNode;
  client?: string;
}

export const ThemeSelectorProvider = ({
  children,
  client,
}: ThemeSelectorProviderProps) => {
  const [theme, setTheme] = useState<ThemeConfig | null>(null);

  useEffect(() => {
    const loadTheme = async () => {
      const defaultTheme = isPrime ? THEMES.prime : THEMES.default;
      const clientTheme = client ? THEMES[client] : undefined;

      if (!client) {
        setTheme(defaultTheme);
        return;
      }

      try {
        const response = await getAppThemeAPI({
          client_url: client,
        });

        setTheme({
          ...(clientTheme || defaultTheme),

          logo: response.logo || clientTheme?.logo || defaultTheme.logo,
          favicon: response.favicon || clientTheme?.favicon || defaultTheme.favicon,

          primary_color:
            response.primary_color ||
            clientTheme?.primary_color ||
            defaultTheme.primary_color,

          banner_color:
            response.banner_color ||
            clientTheme?.banner_color ||
            defaultTheme.banner_color,

          header_text_color:
            response.header_text_color ||
            clientTheme?.header_text_color ||
            defaultTheme.header_text_color,

          showPA: response.is_pa_enabled,
          showPatient: response.is_patient_enabled,
          showPharmacy: response.is_pharmacy_enabled,
          showBilling: response.is_provider_profile_enabled,
          showMedicalClaims: response.is_medical_claims_enabled,
        });

        return;
      } catch (error: any) {
        // Client not found in API or locally
        if (!clientTheme) {
          throw new Error(`Client name '${client}' does not exist`);
        }

        // Client exists in local THEMES
        setTheme(clientTheme);
      }
    };

    loadTheme();
  }, [client]);

  if (!theme) {
    return null;
  }

  return (
    <ThemeSelectorContext.Provider value={theme}>
      {children}
    </ThemeSelectorContext.Provider>
  );
};
